import type { DestroyIntent, DestroyIntentOpts, MasterStateStore } from './MasterStateStore.js';

/**
 * The default, complete `MasterStateStore` — process memory, no redis, and the
 * semantics every conformance case is written against (governing rule 1: no
 * capability is redis-only).
 *
 * Two notes on how memory mode actually uses it:
 *
 * - The DESTROY INTENTS half is live on both backings: `destroyArea` writes
 *   one and every completion/cancel path clears one, unconditionally, with no
 *   `if (this.redis)` anywhere. In memory mode the intents simply die with the
 *   process, which is exactly today's behaviour (a restarted single-process
 *   master has no pending destroys because it has no areas either).
 * - The MEMBERS half is the memory-mode counterpart of the redis spill. In
 *   memory mode `DynamicAreaTable` keeps its member arrays on the records
 *   themselves, byte-identically to today, so the master never routes members
 *   through this class in production — the spill exists only where there is
 *   somewhere to spill TO (see `MasterServer.memberSpillStore`). It is
 *   implemented and conformance-pinned regardless, because a store method that
 *   exists on one backing and not the other is precisely the divergence the
 *   shared suite exists to prevent, and because a memory-backed master IS a
 *   legal spill target for anyone who wants uniform routing.
 *
 * Set semantics (`Set`, mirroring redis `SADD`) rather than an array, so a
 * duplicate join report is one member on both backings.
 */
export class MemoryMasterStateStore implements MasterStateStore {
    private readonly members: Map<string, Set<string>> = new Map();
    private readonly destroyIntents: Map<string, { opts: DestroyIntentOpts, generation: number }> = new Map();

    async addAreaMember(areaId: string, clientId: string): Promise<{ count: number }> {
        const key = String(areaId);
        let set = this.members.get(key);
        if (!set) {
            set = new Set();
            this.members.set(key, set);
        }
        set.add(String(clientId));
        return { count: set.size };
    }

    async removeAreaMember(areaId: string, clientId: string): Promise<{ empty: boolean, count: number }> {
        const key = String(areaId);
        const set = this.members.get(key);
        if (!set) {
            return { empty: true, count: 0 };
        }
        set.delete(String(clientId));
        // Drop the empty container, mirroring redis: a set with no members is
        // not a key that exists.
        if (set.size === 0) {
            this.members.delete(key);
        }
        return { empty: set.size === 0, count: set.size };
    }

    async getAreaMembers(areaId: string): Promise<string[]> {
        const set = this.members.get(String(areaId));
        return set ? [...set] : [];
    }

    async clearAreaMembers(areaId: string): Promise<void> {
        this.members.delete(String(areaId));
    }

    async putDestroyIntent(areaId: string, intent: { opts: DestroyIntentOpts, generation: number }): Promise<void> {
        this.destroyIntents.set(String(areaId), {
            // Copy: the caller's opts object is the live `pendingWhenEmptyDestroys`
            // entry, and a stored intent must be a snapshot, not an alias (the
            // redis backing stores a JSON string, so it snapshots by construction).
            opts: { drain: intent.opts?.drain, reason: intent.opts?.reason },
            generation: Number(intent.generation),
        });
    }

    async getDestroyIntents(): Promise<DestroyIntent[]> {
        return [...this.destroyIntents.entries()].map(([areaId, intent]) => ({
            areaId,
            opts: { drain: intent.opts?.drain, reason: intent.opts?.reason },
            generation: intent.generation,
        }));
    }

    async clearDestroyIntent(areaId: string): Promise<void> {
        this.destroyIntents.delete(String(areaId));
    }
}
